import { cloneElement, isValidElement, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Popover, Table, Tag } from 'antd';
import {
  CheckSquareOffset,
  Circle,
  Cube,
  Info,
  Package,
  Path,
  Printer,
  SquaresFour,
  StackSimple,
  Tote,
  Trash,
  Waves,
} from '@phosphor-icons/react';
import styled from 'styled-components';
import {
  ActionEditDots,
  ActionMenuItem,
  ItemImage,
  OrganisationAvatar,
} from '../../components';
import {
  BoldText,
  ColoredPill,
  ColorText,
  HoverBorderButton,
  MuteText,
  StyledButton,
} from '../../elements';
import { WaveStatusRenderer } from './common';
import { WaveHistory } from './WaveHistory';
import {
  CommonConstant,
  CommonUtility,
  DateFormat,
  DateUtility,
  theme,
  WaveTypes,
} from '../../utility';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250, 500];

const TOP_CELL_STYLE = {
  verticalAlign: 'top',
};

const TABLE_SCROLL_WIDTHS = {
  waves: 1380,
  unassignedAllocations: 1320,
  sortedTotes: 1000,
  sortedSingles: 950,
  expandedOrders: 760,
  labels: 720,
};

const waveIcons = {
  [WaveTypes.shipInBox]: <Package />,
  [WaveTypes.singleItemOrder]: <Cube />,
  [WaveTypes.multiItemOrders]: <SquaresFour />,
};

const NameContainer = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
`;

const ImageContainer = styled.div`
  min-width: 40px;
`;

const StatusPill = styled(ColoredPill)`
  height: 24px;
  padding: 0 10px;
`;

const ButtonSizedDiv = styled.div`
  width: 160px;
`;

const ToteContentsContainer = styled.div`
  width: 500px;
  max-height: 400px;
  overflow: auto;
  font-size: 12px;
  line-height: normal;
  letter-spacing: normal;

  img {
    width: 33px !important;
    height: 33px !important;
  }
`;

const ToteContentsTrigger = styled.div`
  max-width: fit-content;
`;

const EllipsisText = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TransferSignalPopoverContent = styled.div`
  width: 340px;
  max-height: 360px;
  overflow: auto;
  font-size: 12px;

  .section + .section {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid ${theme.colors.semanticBorder};
  }

  .metric-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-top: 4px;

    ${BoldText} {
      word-break: break-word;
      text-align: right;
    }
  }

  .batch-item {
    margin-top: 8px;
    padding: 8px;
    border-radius: 6px;
    background: ${theme.colors.box};
  }
`;

function buildPagination({ filter, totalData, pageChanged, filterChanged }) {
  return {
    current: filter.page,
    pageSize: filter.limit,
    total: totalData,
    showSizeChanger: true,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    showTotal: total => `${total} records`,
    size: 'small',
    onChange: (page, pageSize) => {
      if (pageSize !== filter.limit) {
        filterChanged('limit', pageSize);
      } else {
        pageChanged(page);
      }
    },
  };
}

function toAntSortOrder(sortOrder) {
  if (sortOrder === CommonConstant.sortDirection.ascending) return 'ascend';
  if (sortOrder === CommonConstant.sortDirection.descending) return 'descend';
  return null;
}

function OrderTime({ time, isEnabled }) {
  let timeColor = '';
  const noOfDays = DateUtility.noOfDays(time);
  if (isEnabled) {
    if (noOfDays < 1) {
      timeColor = theme.colors.green;
    } else if (noOfDays <= 2) {
      timeColor = theme.colors.yellow;
    } else if (noOfDays <= 5) {
      timeColor = theme.colors.orange;
    } else if (noOfDays > 5) {
      timeColor = theme.colors.danger;
    }
  }
  return (
    <ColorText color={timeColor}>
      <b>{DateUtility.hourMinuteTime(time)}</b>
    </ColorText>
  );
}

function WaveTypeCell({ waveType }) {
  if (!waveType) return null;

  return (
    <ColoredPill color={theme.colors.grey} mini>
      {isValidElement(waveIcons[waveType]) && cloneElement(waveIcons[waveType])}
      <span>{waveType}</span>
    </ColoredPill>
  );
}

function ContainersCell({ metadata = {} }) {
  return (
    <>
      {!!metadata.palletCount && (
        <div className="d-flex align-items-center text-nowrap">
          <StackSimple className="icon" size={14} color={theme.colors.grey} />
          <span className="ml-2">{metadata.palletCount} Pallet</span>
        </div>
      )}
      {!!metadata.toteCount && (
        <div className="d-flex align-items-center text-nowrap">
          <Tote size={14} className="icon" color={theme.colors.grey} />
          <span className="ml-2">{metadata.toteCount} Totes</span>
        </div>
      )}
    </>
  );
}

function PickerCell({ user }) {
  return user ? (
    <div className="d-flex align-items-center">
      <OrganisationAvatar name={user?.firstName} />
      <span className="ml-1">{user?.firstName}</span>
    </div>
  ) : (
    <MuteText>Not Assigned</MuteText>
  );
}

function WaveContentsCell({ item }) {
  return (
    <div className="d-flex text-nowrap">
      <span className="mr-1">{item.metadata?.itemCount}</span>
      <MuteText>Units</MuteText>
      <span className="mr-1">, {item.orders?.length || 0}</span>
      <MuteText>Orders</MuteText>
    </div>
  );
}

function WaveOrderItemsPopup({ items = [] }) {
  return (
    <div>
      {items.map(item => (
        <div key={item.item_id} className="d-flex align-items-center">
          <ImageContainer>
            <ItemImage
              size="mini"
              item={item.itemDetails}
              imageKey="p_images"
              displayImageKey="display_image"
              extraClasses="m-0"
            />
          </ImageContainer>
          <div className="d-flex flex-column ml-1 mx-4">
            <NameContainer>{item.itemDetails?.p_title}</NameContainer>
            <div>
              <MuteText className="mr-2">SKU</MuteText>
              <span className="text-nowrap">{item.itemDetails?.p_sku}</span>
            </div>
          </div>
          <div className="text-nowrap">x {item.quantity}</div>
        </div>
      ))}
    </div>
  );
}

function ToteContentsPopoverContent({ items = [] }) {
  return (
    <ToteContentsContainer>
      <div className="row mx-0 mb-2 align-items-center">
        <div className="col-8 px-0">
          <MuteText>Product Information</MuteText>
        </div>
        <div className="col-2 text-center px-0">
          <MuteText>Quantity</MuteText>
        </div>
      </div>
      {items.map(item => (
        <div
          key={item.item_id || item.id || item.sku}
          className="row mx-0 mb-2 align-items-center"
        >
          <div className="col-2 px-0">
            <ItemImage size="small" item={item} extraClasses="m-0" />
          </div>
          <div className="col-6">
            <div>
              <NameContainer>{item.title}</NameContainer>
            </div>
            <div>
              <BoldText>{item.sku}</BoldText>
            </div>
          </div>
          <div className="col-2 text-center px-0">
            <span>x{CommonUtility.numberWithCommas(item.qty)}</span>
          </div>
        </div>
      ))}
    </ToteContentsContainer>
  );
}

function ToteContentsPopover({ items = [], totalItems }) {
  return (
    <>
      <Popover content={<ToteContentsPopoverContent items={items} />} placement="top">
        <ToteContentsTrigger
          className="d-flex align-items-center justify-content-center cursor-pointer"
          onClick={event => event.stopPropagation()}
        >
          {CommonUtility.numberWithCommas(items.length)} item(s)
          <Info className="ml-1" />
        </ToteContentsTrigger>
      </Popover>
      <ToteContentsTrigger>
        <MuteText>({CommonUtility.numberWithCommas(totalItems)} unit(s))</MuteText>
      </ToteContentsTrigger>
    </>
  );
}

function ExpandedOrdersTable({ orders = [] }) {
  const columns = [
    {
      title: 'Order ID',
      key: 'order-id',
      width: 220,
      render: (_, order) => order.marketplace_order_display_id,
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 280,
      render: (_, order) => (
        <div className="d-flex align-items-center">
          <OrganisationAvatar name={order.organization?.company_name} />
          <span className="ml-1">{order.organization?.company_name}</span>
        </div>
      ),
    },
    {
      title: 'Contents',
      key: 'contents',
      width: 180,
      render: (_, order) => (
        <div className="d-flex align-items-center ">
          {order.items?.length || 0} Items
          <Popover
            content={<WaveOrderItemsPopup items={order.items || []} />}
            placement="top"
          >
            <Info color={theme.colors.grey} className="ml-1 cursor-pointer" />
          </Popover>
        </div>
      ),
    },
  ];

  return (
    <Table
      rowKey={order => order._id ?? order.marketplace_order_display_id}
      columns={columns}
      dataSource={orders}
      pagination={false}
      size="small"
      scroll={{ x: TABLE_SCROLL_WIDTHS.expandedOrders }}
    />
  );
}

function buildWaveColumns() {
  return [
    {
      title: 'Wave Number',
      key: 'wave-number',
      width: 140,
      render: (_, item) => (
        <Link to={`/app/fulfill/waves/${item._id}`}>{item.waveNumber}</Link>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_, item) => <WaveStatusRenderer status={item.status} />,
    },
    {
      title: 'History',
      key: 'history',
      width: 190,
      render: (_, item) => <WaveHistory wave={item} />,
    },
    {
      title: 'Warehouse',
      key: 'warehouse',
      width: 150,
      responsive: ['lg'],
      render: (_, item) => <EllipsisText>{item.warehouseId?.name}</EllipsisText>,
    },
    {
      title: 'Rule',
      key: 'rule',
      width: 220,
      render: (_, item) => (
        <div className="d-flex align-items-center">
          <Path color={theme.colors.grey} size={17} className="mr-1 icon" />{' '}
          <EllipsisText>{item.waveRuleId?.name}</EllipsisText>
        </div>
      ),
    },
    {
      title: 'Wave Type',
      key: 'wave-type',
      width: 160,
      render: (_, item) => <WaveTypeCell waveType={item.metadata?.waveType} />,
    },
    {
      title: 'Containers',
      key: 'containers',
      width: 135,
      responsive: ['lg'],
      render: (_, item) => <ContainersCell metadata={item.metadata} />,
    },
    {
      title: 'Picker',
      key: 'picker',
      width: 150,
      render: (_, item) => <PickerCell user={item.assignedTo?.userId} />,
    },
    {
      title: 'Contents',
      key: 'details',
      width: 140,
      render: (_, item) => <WaveContentsCell item={item} />,
    },
  ];
}

export function WaveAntTable({
  data,
  loading,
  totalData,
  filter,
  filterChanged,
  pageChanged,
  checkedWaves,
  checkWave,
  unCheckWave,
  allowMerging,
}) {
  const columns = useMemo(() => buildWaveColumns(), []);
  const selectedRowKeys = useMemo(
    () => Object.keys(checkedWaves || {}).filter(waveId => checkedWaves[waveId]),
    [checkedWaves],
  );

  const pagination = buildPagination({
    filter,
    totalData,
    pageChanged,
    filterChanged,
  });

  return (
    <Table
      rowKey="_id"
      columns={columns}
      dataSource={data || []}
      loading={loading}
      size="middle"
      tableLayout="fixed"
      pagination={pagination}
      scroll={{ x: TABLE_SCROLL_WIDTHS.waves }}
      style={{
        borderRadius: 6,
        border: `1px solid ${theme.colors.semanticBorder}`,
      }}
      locale={{ emptyText: 'No Data Found' }}
      rowSelection={
        allowMerging
          ? {
              selectedRowKeys,
              preserveSelectedRowKeys: true,
              onSelect: (record, selected) => {
                if (selected) {
                  checkWave(record);
                } else {
                  unCheckWave(record);
                }
              },
              onSelectAll: (selected, _selectedRows, changeRows) => {
                changeRows.forEach(record => {
                  if (selected) {
                    checkWave(record);
                  } else {
                    unCheckWave(record);
                  }
                });
              },
            }
          : undefined
      }
      expandable={{
        rowExpandable: record => CommonUtility.isValidArray(record.orders),
        expandedRowRender: record => <ExpandedOrdersTable orders={record.orders || []} />,
      }}
    />
  );
}

function AllocationConfigCell({ item }) {
  return item.requiresTote ? (
    <Tag color={theme.colors.green}>Tote</Tag>
  ) : (
    <Tag color={theme.colors.darkBlue}>Pallet</Tag>
  );
}

function getTransferSignal(item) {
  if (!item) return null;

  if (item.suggestedTransfer || item.batchTransfer || item.scheduledTransfer) {
    return item;
  }

  return (
    item.transferSignal ||
    item.transferSignals ||
    item.transferStatus ||
    item.transferSummary ||
    null
  );
}

function PopoverMetric({ label, value }) {
  return (
    <div className="metric-row">
      <MuteText>{label}</MuteText>
      <BoldText>{value ?? 0}</BoldText>
    </div>
  );
}

function TransferSignalDetails({ signal }) {
  const suggestedTransfer = signal?.suggestedTransfer || {};
  const batchTransfer = signal?.batchTransfer || {};
  const scheduledTransfer = signal?.scheduledTransfer || {};

  return (
    <TransferSignalPopoverContent>
      <div className="section">
        <BoldText>Suggested Transfer</BoldText>
        <PopoverMetric
          label="Status"
          value={suggestedTransfer.hasSuggestedTransfer ? 'Suggested' : 'None'}
        />
        <PopoverMetric
          label="Suggested Quantity"
          value={suggestedTransfer.totalSuggestedQuantity}
        />
        <PopoverMetric label="Lines" value={suggestedTransfer.lineCount} />
      </div>

      <div className="section">
        <BoldText>Batch Transfer</BoldText>
        <PopoverMetric
          label="Status"
          value={batchTransfer.hasBatchTransfer ? 'In Progress' : 'None'}
        />
        <PopoverMetric
          label="Suggested Quantity"
          value={batchTransfer.totalSuggestedQuantity}
        />
        <PopoverMetric label="Items" value={batchTransfer.itemCount} />
        {batchTransfer.items?.map(batchItem => (
          <div
            className="batch-item"
            key={batchItem.transferLineId || batchItem.replenishmentWaveId}
          >
            <PopoverMetric
              label="Wave"
              value={batchItem.waveNumber || batchItem.replenishmentWaveId}
            />
            <PopoverMetric label="Status" value={batchItem.waveStatus} />
            <PopoverMetric
              label="Suggested Quantity"
              value={batchItem.suggestedQuantity}
            />
          </div>
        ))}
      </div>

      <div className="section">
        <BoldText>Scheduled Transfer</BoldText>
        <PopoverMetric
          label="Status"
          value={scheduledTransfer.hasScheduledTransfer ? 'Scheduled' : 'None'}
        />
        <PopoverMetric label="Quantity" value={scheduledTransfer.totalQuantity} />
        <PopoverMetric label="Transfers" value={scheduledTransfer.transferCount} />
      </div>
    </TransferSignalPopoverContent>
  );
}

function TransferSignalCell({ item }) {
  const signal = getTransferSignal(item);
  const suggestedTransfer = signal?.suggestedTransfer;
  const batchTransfer = signal?.batchTransfer;
  const scheduledTransfer = signal?.scheduledTransfer;

  const isBeingWorkedOn =
    batchTransfer?.hasBatchTransfer || scheduledTransfer?.hasScheduledTransfer;
  const hasSuggestedTransfer = suggestedTransfer?.hasSuggestedTransfer;

  if (!isBeingWorkedOn && !hasSuggestedTransfer) {
    return <MuteText>N/A</MuteText>;
  }

  const color = isBeingWorkedOn ? theme.colors.green : theme.colors.yellow;
  const label = isBeingWorkedOn ? 'Being Worked On' : 'Transfer Suggested';

  return (
    <Popover
      content={<TransferSignalDetails signal={signal} />}
      placement="top"
      autoAdjustOverflow
    >
      <span className="d-inline-block">
        <Tag color={color} className="cursor-pointer text-nowrap">
          {label}
        </Tag>
      </span>
    </Popover>
  );
}

function buildUnassignedColumns({
  assignAllocation,
  fulfillOrder,
  onClickCancel,
  activeSortField,
  activeAntSortOrder,
}) {
  return [
    {
      title: 'Product Description',
      key: 'product',
      width: '20%',
      onCell: () => ({ style: TOP_CELL_STYLE }),
      render: (_, item) => (
        <div className="d-flex align-items-center">
          <ItemImage
            imageKey="images"
            displayImageKey="display_image"
            item={item.inventory.product}
            size="mini"
          />
          <div className="ml-4">
            <div>{item.inventory.product?.name}</div>
            <div className="d-flex">
              <MuteText className="mr-2 text-nowrap">SKU</MuteText>
              <BoldText>{item.inventory.product?.sku}</BoldText>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Config',
      key: 'config',
      width: '7%',
      render: (_, item) => <AllocationConfigCell item={item} />,
    },
    {
      title: 'Transfer',
      key: 'transfer',
      width: '13%',
      render: (_, item) => <TransferSignalCell item={item} />,
    },
    {
      title: 'Created',
      key: 'createdAt',
      dataIndex: 'createdAt',
      width: '15%',
      sorter: true,
      sortOrder: activeSortField === 'createdAt' ? activeAntSortOrder : null,
      render: (_, item) => (
        <>
          <OrderTime time={item.createdAt} isEnabled />
          <div>
            <MuteText>
              {DateUtility.formatDate(item.createdAt, DateFormat.barChartMonth)}
            </MuteText>
          </div>
        </>
      ),
    },
    {
      title: 'Zone',
      key: 'zone',
      width: '9%',
      render: (_, item) => (
        <Tag color={item.inventory?.zone?.color}>{item.inventory?.zone?.code}</Tag>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      width: '17%',
      render: (_, item) => (
        <div>
          <div>
            <MuteText>Rack:</MuteText>
            <span className="ml-2">{item.inventory?.rack?.code}</span>
          </div>
          <div>
            <MuteText>Bay:</MuteText>
            <span className="ml-2">{item.inventory?.location?.code}</span>
          </div>
        </div>
      ),
    },
    {
      title: 'Details',
      key: 'details',
      width: '15%',
      render: (_, item) => (
        <div>
          <div>
            <MuteText>Order ID:</MuteText>
            <span className="ml-2">{item.order?.marketplaceOrderDisplayId}</span>
          </div>
          {item?.customerOrganization?.name && (
            <div className="d-flex align-items-center">
              <OrganisationAvatar
                name={item?.customerOrganization?.name}
                image={item?.customerOrganization?.logo?.url}
                mini
              />
              <span className="ml-1">{item?.customerOrganization?.name}</span>
            </div>
          )}
          <ActionEditDots preventChangingBg on="hover" position="left center">
            <ActionMenuItem onClick={() => assignAllocation(item)}>
              <Waves />
              Assign To Wave
            </ActionMenuItem>
            <ActionMenuItem onClick={() => fulfillOrder(item)}>
              <CheckSquareOffset /> Mark Fulfilled
            </ActionMenuItem>
            <ActionMenuItem className="danger" onClick={() => onClickCancel(item)}>
              <Trash /> Cancel Allocation
            </ActionMenuItem>
          </ActionEditDots>
        </div>
      ),
    },
  ];
}

export function UnassignedAllocationsAntTable({
  data,
  loading,
  totalData,
  filter,
  filterChanged,
  onSortChange,
  pageChanged,
  allocationSelection,
  toggleAllocation,
  assignAllocation,
  fulfillOrder,
  onClickCancel,
}) {
  const activeSortField = filter.sort?.sortBy;
  const activeAntSortOrder = toAntSortOrder(filter.sort?.sortOrder);
  const columns = useMemo(
    () =>
      buildUnassignedColumns({
        assignAllocation,
        fulfillOrder,
        onClickCancel,
        activeSortField,
        activeAntSortOrder,
      }),
    [assignAllocation, fulfillOrder, onClickCancel, activeSortField, activeAntSortOrder],
  );
  const selectedRowKeys = useMemo(
    () =>
      Object.keys(allocationSelection || {}).filter(
        key => allocationSelection[key]?.selected,
      ),
    [allocationSelection],
  );

  const pagination = buildPagination({
    filter,
    totalData,
    pageChanged,
    filterChanged,
  });

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={data || []}
      loading={loading}
      style={{
        borderRadius: 6,
        border: `1px solid ${theme.colors.semanticBorder}`,
      }}
      size="middle"
      tableLayout="fixed"
      pagination={pagination}
      scroll={{ x: TABLE_SCROLL_WIDTHS.unassignedAllocations }}
      sortDirections={['ascend', 'descend', 'ascend']}
      onChange={(_pagination, _filters, sorter, extra) => {
        if (extra.action !== 'sort') return;

        const single = Array.isArray(sorter) ? sorter[0] : sorter;
        const field = single?.field || 'createdAt';
        onSortChange({
          [String(field)]:
            single?.order === 'ascend'
              ? CommonConstant.sortDirection.ascending
              : CommonConstant.sortDirection.descending,
        });
      }}
      locale={{ emptyText: 'No Data Found' }}
      rowSelection={{
        selectedRowKeys,
        preserveSelectedRowKeys: true,
        onSelect: (record, selected) => {
          if (!record?.id) return;
          if (!!allocationSelection[record.id]?.selected !== selected) {
            toggleAllocation(record, selected);
          }
        },
        onSelectAll: (selected, _selectedRows, changeRows) => {
          changeRows.forEach(record => {
            if (!record?.id) return;
            if (!!allocationSelection[record.id]?.selected !== selected) {
              toggleAllocation(record, selected);
            }
          });
        },
      }}
    />
  );
}

function buildSortedToteColumns({ fulfillTote, warehouseId }) {
  return [
    {
      title: 'Tote Number',
      key: 'tote-number',
      width: '18%',
      render: (_, item) =>
        item.toteNumber ? (
          <Link
            to={`/app/fulfill/packing/details/${item.toteNumber}/?warehouseId=${warehouseId}`}
            rel="noopener noreferrer"
          >
            {item.toteNumber}
          </Link>
        ) : (
          <MuteText>N/A</MuteText>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      width: '16%',
      render: (_, item) => (
        <StatusPill color={theme.colors.orange}>
          <Circle weight="fill" />
          {CommonUtility.toTitleCase(item.status)}
        </StatusPill>
      ),
    },
    {
      title: 'Allocated At',
      key: 'updatedAt',
      width: '20%',
      render: (_, item) => DateUtility.formatDate(item.updatedAt, DateFormat.dateTime),
    },
    {
      title: 'Contents',
      key: 'contents',
      width: '24%',
      render: (_, item) => {
        const formattedItems =
          item.contents?.map(content => ({
            ...(content.product || {}),
            id: content.product?.id || content.product?._id,
            title: content.product?.name,
            qty: content.quantity,
            pImages: {
              displayImage: content.product?.image,
            },
          })) || [];
        const totalItems =
          item.contents?.reduce((qty, content) => qty + content.quantity, 0) || 0;

        return <ToteContentsPopover totalItems={totalItems} items={formattedItems} />;
      },
    },
    {
      title: '',
      key: 'actions',
      width: '22%',
      render: (_, item) => {
        const label = item?.shipping?.labels?.[0];

        return (
          <div className="d-flex justify-content-end flex-wrap">
            {label && (
              <a download href={label?.url} target="_blank" rel="noreferrer">
                <HoverBorderButton className="flex-btn text-nowrap">
                  <Printer className="mr-2 mb-1" size={17} /> Print Label
                </HoverBorderButton>
              </a>
            )}
            {item.status === 'ready_to_pack' && (
              <StyledButton
                onClick={() => fulfillTote(item)}
                className="ml-2 flex-btn text-nowrap"
              >
                <CheckSquareOffset className="mr-2 mb-1" size={17} /> Mark Fulfilled
              </StyledButton>
            )}
          </div>
        );
      },
    },
  ];
}

export function SortedToteAntTable({
  data,
  loading,
  totalData,
  filter,
  filterChanged,
  pageChanged,
  fulfillTote,
  warehouseId,
}) {
  const columns = useMemo(
    () => buildSortedToteColumns({ fulfillTote, warehouseId }),
    [fulfillTote, warehouseId],
  );
  const pagination = buildPagination({
    filter,
    totalData,
    pageChanged,
    filterChanged,
  });

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={data || []}
      loading={loading}
      size="middle"
      tableLayout="fixed"
      style={{
        borderRadius: 6,
        border: `1px solid ${theme.colors.semanticBorder}`,
      }}
      pagination={pagination}
      scroll={{ x: TABLE_SCROLL_WIDTHS.sortedTotes }}
      locale={{ emptyText: 'No Data Found' }}
    />
  );
}

function ProductCell({ item }) {
  const product = item.inventory?.product || {};

  return (
    <div className="d-flex">
      <ItemImage item={product} displayImageKey="display_image" imageKey="images" />
      <div className="d-flex flex-column ml-4">
        <span>{product.name}</span>
        <div className="d-flex mt-2">
          <MuteText>SKU</MuteText>
          <BoldText className="ml-2">{product.sku}</BoldText>
        </div>
      </div>
    </div>
  );
}

function SortedSingleActions({ item, fulfillOrder, expanded, setExpanded }) {
  const labels = item.shipping?.labels || [];
  const isMultiple = labels.length > 1;

  return (
    <div className="d-flex justify-content-end flex-wrap">
      {isMultiple ? (
        <HoverBorderButton
          className="flex-btn text-nowrap mr-2"
          onClick={() => setExpanded(item.id, !expanded)}
        >
          {expanded ? 'Hide Labels' : 'Multiple Labels'}
        </HoverBorderButton>
      ) : (
        labels?.[0]?.url && (
          <a download href={labels?.[0]?.url} target="_blank" rel="noreferrer">
            <HoverBorderButton className="flex-btn text-nowrap">
              <Printer className="mr-2 mb-1" size={17} /> Print Label
            </HoverBorderButton>
          </a>
        )
      )}
      <StyledButton
        onClick={() => fulfillOrder(item)}
        className="ml-2 flex-btn text-nowrap"
      >
        <CheckSquareOffset className="mr-2 mb-1" size={17} /> Mark Fulfilled
      </StyledButton>
    </div>
  );
}

function MultipleLabelsTable({ labels = [] }) {
  const columns = [
    {
      title: 'Tracking No.',
      key: 'tracking',
      render: (_, label) => (
        <>
          <MuteText className="mr-1">Tracking No.</MuteText> {label.tracking}
        </>
      ),
    },
    {
      title: 'Carrier',
      key: 'carrier',
      align: 'center',
      render: (_, label) => (
        <>
          <MuteText className="mr-1">Carrier</MuteText>{' '}
          {CommonUtility.toTitleCase(label.carrier)}
        </>
      ),
    },
    {
      title: 'Service',
      key: 'service',
      align: 'center',
      render: (_, label) => (
        <>
          <MuteText className="mr-1">Service</MuteText> {label.service?.key}
        </>
      ),
    },
    {
      title: '',
      key: 'actions',
      render: (_, label) => (
        <div className="d-flex justify-content-center">
          <a download href={label?.url} target="_blank" rel="noreferrer">
            <HoverBorderButton className="flex-btn text-nowrap">
              <Printer className="mr-2 mb-1" size={17} /> Print Label
            </HoverBorderButton>
          </a>
          <ButtonSizedDiv />
        </div>
      ),
    },
  ];

  return (
    <Table
      rowKey="tracking"
      columns={columns}
      dataSource={labels}
      pagination={false}
      size="small"
      scroll={{ x: TABLE_SCROLL_WIDTHS.labels }}
    />
  );
}

function buildSortedSingleColumns({ fulfillOrder, expandedRowKeys, setExpanded }) {
  return [
    {
      title: 'Item',
      key: 'item',
      width: '40%',
      render: (_, item) => <ProductCell item={item} />,
    },
    {
      title: 'Quantity',
      key: 'quantity',
      width: '14%',
      align: 'center',
      render: (_, item) => item.quantity,
    },
    {
      title: 'Status',
      key: 'status',
      width: '16%',
      align: 'center',
      render: (_, item) => (
        <StatusPill color={theme.colors.orange}>
          <Circle weight="fill" />
          {CommonUtility.toTitleCase(item.status)}
        </StatusPill>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: '30%',
      render: (_, item) => (
        <SortedSingleActions
          item={item}
          fulfillOrder={fulfillOrder}
          expanded={expandedRowKeys.includes(item.id)}
          setExpanded={setExpanded}
        />
      ),
    },
  ];
}

export function SortedSingleAntTable({
  data,
  loading,
  totalData,
  filter,
  filterChanged,
  pageChanged,
  fulfillOrder,
}) {
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const setExpanded = (id, expanded) => {
    setExpandedRowKeys(keys =>
      expanded ? [...new Set([...keys, id])] : keys.filter(key => key !== id),
    );
  };

  const columns = useMemo(
    () =>
      buildSortedSingleColumns({
        fulfillOrder,
        expandedRowKeys,
        setExpanded,
      }),
    [fulfillOrder, expandedRowKeys],
  );
  const pagination = buildPagination({
    filter,
    totalData,
    pageChanged,
    filterChanged,
  });

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={data || []}
      loading={loading}
      size="middle"
      tableLayout="fixed"
      style={{
        borderRadius: 6,
        border: `1px solid ${theme.colors.semanticBorder}`,
      }}
      pagination={pagination}
      scroll={{ x: TABLE_SCROLL_WIDTHS.sortedSingles }}
      locale={{ emptyText: 'No Data Found' }}
      expandable={{
        expandedRowKeys,
        onExpandedRowsChange: keys => setExpandedRowKeys(keys),
        rowExpandable: record => (record.shipping?.labels || []).length > 1,
        expandedRowRender: record => (
          <MultipleLabelsTable labels={record.shipping?.labels || []} />
        ),
      }}
    />
  );
}
